import type { TradeLang } from './trade';

/**
 * Rà soát hợp đồng mua / PI bằng AI (nhánh C Xuất nhập khẩu, DEC-545, bảng contract_reviews migration 0241,
 * docs/phases/P10/xnk-ra-soat-hop-dong.md).
 *
 * Tiết kiệm token + giữ AI_RULES §5 (không gửi giá nhập ra AI): ERP tự bóc chữ (PDF có lớp chữ, Word), chuẩn hóa,
 * ẩn số tiền và gộp bảng hàng, chia điều khoản [C1]…[Cn]; AI chỉ trả các điều bất lợi (tham chiếu số điều, không chép lại
 * toàn văn) + câu sửa. Bản sửa hoàn chỉnh do ERP ghép lại (thay điều đã chấp nhận), không bắt AI viết lại cả hợp đồng.
 */

export const CONTRACT_REVIEW_STATUSES = ['uploaded', 'reviewing', 'reviewed', 'sent', 'agreed', 'signed', 'failed'] as const;
export type ContractReviewStatus = (typeof CONTRACT_REVIEW_STATUSES)[number];
export const CONTRACT_REVIEW_STATUS_LABELS: Record<ContractReviewStatus, string> = {
  uploaded: 'Đã tải lên',
  reviewing: 'AI đang đọc',
  reviewed: 'Đã rà soát',
  sent: 'Đã gửi bản sửa',
  agreed: 'Đối tác đồng ý',
  signed: 'Đã ký, lưu sổ',
  failed: 'AI đọc lỗi',
};

export const CONTRACT_REVIEW_SIDES = ['buyer', 'seller'] as const;
export type ContractReviewSide = (typeof CONTRACT_REVIEW_SIDES)[number];
export const CONTRACT_REVIEW_SIDE_LABELS: Record<ContractReviewSide, string> = {
  buyer: 'Minh Tiến là bên mua (nhập khẩu / mua NCC)',
  seller: 'Minh Tiến là bên bán (xuất khẩu)',
};

export const CLAUSE_RISKS = ['high', 'medium', 'low'] as const;
export type ClauseRisk = (typeof CLAUSE_RISKS)[number];
export const CLAUSE_RISK_LABELS: Record<ClauseRisk, string> = { high: 'Rủi ro cao', medium: 'Nên sửa', low: 'Lưu ý' };

export const CLAUSE_TOPICS = [
  'price',
  'payment',
  'delivery',
  'penalty',
  'quality',
  'inspection',
  'quantity',
  'documents',
  'claim',
  'warranty',
  'liability',
  'force_majeure',
  'law',
  'incoterms',
  'other',
] as const;
export type ClauseTopic = (typeof CLAUSE_TOPICS)[number];

export interface ClauseTopicDef {
  label: string;
  /** Chuẩn có lợi cho bên mua (đưa vào lời dặn AI, hiện ở màn hình để NV tự đối chiếu) */
  buyer: string;
  /** Chuẩn có lợi cho bên bán (Minh Tiến xuất khẩu) */
  seller: string;
  /** Điều khoản pháp lý: luôn đánh dấu cần luật sư kiểm trước khi ký */
  lawyer: boolean;
}

/**
 * Chuẩn đối chiếu (Claude soạn theo thông lệ mua thép / thiết bị nhập khẩu, Luật Thương mại 2005, CISG, Incoterms 2020, UCP 600).
 * ⚖ Không thay ý kiến luật sư; con số (8 %, 0,5 %/tuần, ±3 %…) là mặc định để đàm phán, anh sửa theo chính sách công ty.
 */
export const CLAUSE_TOPIC_DEFS: Record<ClauseTopic, ClauseTopicDef> = {
  price: {
    label: 'Giá, điều chỉnh giá',
    buyer: 'Giá cố định đến hết giao hàng; không cho bên bán tăng giá đơn phương (nguyên liệu, tỷ giá, cước); phụ phí phải ghi rõ trong hợp đồng.',
    seller: 'Cho điều chỉnh giá khi giá nguyên liệu / cước / tỷ giá biến động quá mức thỏa thuận; giá có hiệu lực theo thời hạn chào.',
    lawyer: false,
  },
  payment: {
    label: 'Thanh toán',
    buyer: 'Cọc ≤ 30 %, phần còn lại khi nhận bộ chứng từ gốc (B/L, Invoice, PL, C/O, MTC) hoặc L/C trả sau; không trả 100 % trước; phí ngân hàng ngoài Việt Nam do bên bán chịu.',
    seller: 'Cọc ≥ 30 % trước sản xuất, phần còn lại trước khi giao B/L hoặc L/C trả ngay không hủy ngang của ngân hàng hạng tốt; lãi chậm trả.',
    lawyer: false,
  },
  delivery: {
    label: 'Thời hạn giao hàng',
    buyer: 'Ngày giao cố định (ngày xếp hàng / ngày B/L), không ghi "khoảng" hay "tùy lịch tàu"; trễ quá 30 ngày bên mua được hủy và lấy lại cọc; không giao từng phần nếu chưa đồng ý.',
    seller: 'Thời hạn giao tính từ ngày nhận cọc / L/C hợp lệ; được giao từng phần, chuyển tải; dung sai ngày giao hợp lý.',
    lawyer: false,
  },
  penalty: {
    label: 'Phạt vi phạm, bồi thường',
    buyer: 'Phạt giao chậm 0,5 %/tuần, tối đa 8 % giá trị phần vi phạm (trần Điều 301 Luật Thương mại 2005 nếu áp luật Việt Nam), ngoài phạt còn được đòi bồi thường thiệt hại; phạt hai chiều cân bằng.',
    seller: 'Phạt có trần thấp, loại trừ thiệt hại gián tiếp; tổng trách nhiệm không vượt giá trị hợp đồng.',
    lawyer: true,
  },
  quality: {
    label: 'Chất lượng, tiêu chuẩn, mill test',
    buyer: 'Ghi rõ mác thép / tiêu chuẩn (JIS, ASTM, EN, GB, TCVN), dung sai kích thước, xuất xứ nhà máy; kèm chứng chỉ nhà máy (MTC theo EN 10204 3.1) cho từng lô; hàng mới 100 %.',
    seller: 'Chất lượng theo MTC của nhà máy là căn cứ cuối cùng; dung sai theo tiêu chuẩn nhà máy.',
    lawyer: false,
  },
  inspection: {
    label: 'Giám định',
    buyer: 'Giám định tại cảng đến bởi bên độc lập (Vinacontrol, SGS…) là căn cứ cuối cùng; bên mua được giám định trước khi xếp hàng; chi phí giám định do bên bán chịu nếu hàng không đạt.',
    seller: 'Giám định tại cảng xếp hàng (do bên bán chỉ định) là căn cứ cuối cùng.',
    lawyer: false,
  },
  quantity: {
    label: 'Số lượng, dung sai, trọng lượng',
    buyer: 'Dung sai số lượng ±3 % do bên mua chọn, thanh toán theo số lượng thực giao; cân thực tế (không theo barem lý thuyết) hoặc ghi rõ cách tính trọng lượng.',
    seller: 'Dung sai ±10 % do bên bán chọn; trọng lượng theo barem lý thuyết / phiếu cân nhà máy.',
    lawyer: false,
  },
  documents: {
    label: 'Chứng từ, C/O, mã HS',
    buyer: 'Bên bán cấp đủ bộ chứng từ gốc: Invoice, PL, B/L, C/O mẫu đúng (Form E / D / AK / AJ / VJ…) để hưởng thuế ưu đãi, MTC; mã HS trên chứng từ phải khớp hợp đồng; thiếu / sai C/O làm mất ưu đãi thì bên bán bồi hoàn phần thuế chênh.',
    seller: 'Bộ chứng từ theo danh sách đã thỏa thuận; không chịu trách nhiệm mã HS do bên mua khai.',
    lawyer: false,
  },
  claim: {
    label: 'Khiếu nại',
    buyer: 'Hạn khiếu nại chất lượng ≥ 60 ngày, số lượng ≥ 30 ngày kể từ ngày hàng về kho bên mua; bên bán trả lời trong 15 ngày, im lặng là chấp nhận; bồi hoàn bằng tiền hoặc thay hàng, chịu chi phí phát sinh.',
    seller: 'Hạn khiếu nại ngắn (số lượng 15 ngày, chất lượng 30 ngày kể từ ngày dỡ hàng), phải kèm biên bản giám định độc lập.',
    lawyer: false,
  },
  warranty: {
    label: 'Bảo hành',
    buyer: 'Bảo hành ≥ 12 tháng kể từ ngày nghiệm thu / đưa vào sử dụng (thiết bị); thay mới hàng lỗi, chịu cước hai chiều.',
    seller: 'Bảo hành có thời hạn ngắn, chỉ sửa / thay linh kiện lỗi do sản xuất, loại trừ lắp đặt sai.',
    lawyer: false,
  },
  liability: {
    label: 'Giới hạn trách nhiệm, chuyển rủi ro, quyền sở hữu',
    buyer: 'Không chấp nhận điều loại trừ / giới hạn trách nhiệm một chiều của bên bán; rủi ro chuyển theo Incoterms đã chọn; bên bán bảo đảm hàng không vi phạm sở hữu trí tuệ.',
    seller: 'Giới hạn trách nhiệm tối đa bằng giá trị hợp đồng, loại trừ thiệt hại gián tiếp; giữ quyền sở hữu tới khi nhận đủ tiền.',
    lawyer: true,
  },
  force_majeure: {
    label: 'Bất khả kháng',
    buyer: 'Liệt kê sự kiện cụ thể; không tính biến động giá, thiếu nguyên liệu, đình công nội bộ của bên bán là bất khả kháng; thông báo trong 7 ngày kèm giấy xác nhận của phòng thương mại; kéo dài quá 60 ngày thì được hủy và lấy lại cọc.',
    seller: 'Định nghĩa rộng (gồm thiếu nguyên liệu, cắt điện, chính sách xuất khẩu); kéo dài thời hạn tương ứng.',
    lawyer: true,
  },
  law: {
    label: 'Luật áp dụng, trọng tài',
    buyer: 'Luật Việt Nam hoặc CISG; trọng tài VIAC (TP.HCM), ngôn ngữ trọng tài tiếng Việt / tiếng Anh; tránh tòa án / trọng tài nước bên bán (CIETAC, KCAB, JCAA) và luật nước bên bán.',
    seller: 'Luật Việt Nam, trọng tài VIAC; hoặc trọng tài nước thứ ba trung lập (SIAC Singapore).',
    lawyer: true,
  },
  incoterms: {
    label: 'Incoterms, nơi giao, bảo hiểm',
    buyer: 'Ghi rõ Incoterms 2020 + địa điểm (FOB cảng xếp / CFR, CIF cảng HCM); CIF thì bảo hiểm mức 110 % giá trị, điều kiện A (ICC A); phí local tại cảng xếp do bên bán chịu.',
    seller: 'EXW / FCA / FOB cảng xếp; phí phát sinh sau khi giao lên tàu do bên mua chịu.',
    lawyer: false,
  },
  other: { label: 'Điều khác', buyer: 'Điều chỉnh / chấm dứt chỉ bằng văn bản hai bên ký; bản tiếng Việt / tiếng Anh có giá trị ngang nhau.', seller: '', lawyer: false },
};

/** Chủ đề phải có trong hợp đồng mua; không thấy thì AI báo "thiếu điều khoản" */
export const REQUIRED_CLAUSE_TOPICS: readonly ClauseTopic[] = ['price', 'payment', 'delivery', 'penalty', 'quality', 'inspection', 'quantity', 'documents', 'claim', 'force_majeure', 'law', 'incoterms'];

export const CONTRACT_REVIEW_DECISIONS = ['pending', 'accepted', 'rejected'] as const;
export type ContractReviewDecision = (typeof CONTRACT_REVIEW_DECISIONS)[number];
export const CONTRACT_REVIEW_DECISION_LABELS: Record<ContractReviewDecision, string> = { pending: 'Chưa xem', accepted: 'Đưa vào bản sửa', rejected: 'Bỏ qua' };

/** Một điều bất lợi (lưu ở contract_reviews.findings) */
export interface ContractFinding {
  /** Số điều do ERP đánh (C1…Cn); null = hợp đồng thiếu điều này, bản sửa thêm điều mới */
  ref: string | null;
  /** Tên điều như trên hợp đồng ("Article 5 – Payment", "Điều 4"…) */
  clause: string;
  topic: ClauseTopic;
  risk: ClauseRisk;
  /** Vì sao bất lợi (tiếng Việt) */
  issue: string;
  proposalVi: string;
  /** Câu sửa bằng tiếng của đối tác (= proposalVi khi đối tác dùng tiếng Việt) */
  proposalPartner: string;
  /** ⚖ Cần luật sư kiểm trước khi ký */
  lawyerCheck: boolean;
  decision: ContractReviewDecision;
}

export const MAX_CONTRACT_REVIEW_BYTES = 15 * 1024 * 1024;
/** Chữ gửi AI tối đa (sau khi chuẩn hóa + ẩn bảng hàng) ≈ 30 nghìn token; dài hơn thì tách phụ lục */
export const MAX_REVIEW_CHARS = 100_000;

// ───────────────────────── Ngôn ngữ ─────────────────────────

/** Đoán ngôn ngữ chính của văn bản theo bảng chữ (Hangul → ko, kana → ja, Hán → zh, chữ Việt có dấu → vi, còn lại en). */
export function detectContractLang(text: string): TradeLang {
  const sample = text.slice(0, 20000);
  const count = (re: RegExp) => sample.match(re)?.length ?? 0;
  const hangul = count(/[가-힯]/g);
  const kana = count(/[぀-ヿ]/g);
  const han = count(/[一-鿿]/g);
  const latin = count(/[a-z]/gi);
  const viet = count(/[ăâđêôơưạảấầẩẫậắằẳẵặẹẻẽếềểễệỉịọỏốồổỗộớờởỡợụủứừửữựỳỵỷỹ]/gi);
  const cjk = hangul + kana + han;
  if (cjk > latin * 0.3) {
    if (hangul >= kana && hangul >= han * 0.3) return 'ko';
    if (kana > han * 0.05) return 'ja';
    return 'zh';
  }
  return viet > latin * 0.03 ? 'vi' : 'en';
}

// ───────────────────────── Chuẩn hóa + chia điều ─────────────────────────

const PAGE_LINE = /^\s*(?:-\s*\d+\s*-|page\s*\d+(?:\s*(?:of|\/)\s*\d+)?|trang\s*\d+(?:\s*\/\s*\d+)?|第\s*\d+\s*页(?:\s*共\s*\d+\s*页)?|\d+\s*\/\s*\d+)\s*$/i;

/** Gom khoảng trắng, bỏ dòng số trang và dòng đầu / chân trang lặp lại (≥ 3 lần, ngắn). */
export function normalizeContractText(text: string): string {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .replace(/[\t\u00a0\u3000]+/g, ' ')
    .split('\n')
    .map((l) => l.replace(/ {2,}/g, ' ').trim());
  const freq = new Map<string, number>();
  for (const l of lines) if (l && l.length <= 80) freq.set(l, (freq.get(l) ?? 0) + 1);
  const out: string[] = [];
  for (const l of lines) {
    if (PAGE_LINE.test(l)) continue;
    if (l && l.length <= 80 && (freq.get(l) ?? 0) >= 3 && !/^\d/.test(l)) continue;
    if (!l && out[out.length - 1] === '') continue;
    out.push(l);
  }
  return out.join('\n').trim();
}

/** Dòng mở đầu một điều khoản: Article 5 / ARTICLE V / Clause 3 / Điều 4 / 第五条 / 제5조 / "5." "5)" "5 –" + chữ HOA */
const HEADING =
  /^(?:(?:article|clause|section|điều|dieu)\s*(?:\d{1,2}|[ivxlc]{1,6})\b|第\s*[一二三四五六七八九十百\d]{1,4}\s*[条條章]|제\s*\d{1,2}\s*조|\d{1,2}\s*[.)、．]\s*(?!\d)\S|\d{1,2}\s+[A-ZĐ一-鿿])/i;

export interface ContractClause {
  ref: string;
  heading: string;
  text: string;
}

/**
 * Chia văn bản thành điều khoản theo dòng tiêu đề điều. Không thấy tiêu đề nào thì chia theo đoạn ~1.500 ký tự.
 * Phần trước điều đầu tiên (tên HĐ, các bên) thành C0 khi có nội dung.
 */
export function splitContractClauses(text: string): ContractClause[] {
  const lines = text.split('\n');
  const starts: number[] = [];
  lines.forEach((l, i) => {
    if (l.length <= 160 && HEADING.test(l) && !isTableRow(l)) starts.push(i);
  });
  const pieces: { heading: string; body: string }[] = [];
  if (starts.length >= 2) {
    const pre = lines.slice(0, starts[0]).join('\n').trim();
    if (pre) pieces.push({ heading: 'Phần đầu (các bên, căn cứ)', body: pre });
    starts.forEach((s, k) => {
      const body = lines.slice(s, starts[k + 1] ?? lines.length).join('\n').trim();
      pieces.push({ heading: (lines[s] ?? '').slice(0, 120), body });
    });
  } else {
    let buf = '';
    for (const para of text.split(/\n{2,}/)) {
      if (buf && buf.length + para.length > 1500) {
        pieces.push({ heading: (buf.split('\n')[0] ?? '').slice(0, 120), body: buf.trim() });
        buf = '';
      }
      buf += (buf ? '\n\n' : '') + para;
    }
    if (buf.trim()) pieces.push({ heading: (buf.split('\n')[0] ?? '').slice(0, 120), body: buf.trim() });
  }
  const hasPre = starts.length >= 2 && pieces[0]?.heading.startsWith('Phần đầu');
  return pieces.map((p, i) => ({ ref: `C${hasPre ? i : i + 1}`, heading: p.heading, text: p.body }));
}

// ───────────────────────── Ẩn số tiền (AI_RULES §5) ─────────────────────────

const CUR = '(?:USD|US\\$|CNY|RMB|JPY|KRW|EUR|VND|VNĐ|SGD|THB|TWD|\\$|¥|€|₫|₩|元|円|원|đồng|dong)';
const AMOUNT = '\\d{1,3}(?:[.,\\s]\\d{3})+(?:[.,]\\d+)?|\\d+(?:[.,]\\d+)?';
const MONEY = new RegExp(`${CUR}\\s?(?:${AMOUNT})|(?:${AMOUNT})\\s?${CUR}`, 'gi');

export interface MaskedText {
  text: string;
  /** [TIỀN-1] → "USD 650.00" để trả lại số thật vào câu sửa */
  amounts: Record<string, string>;
  /** Số dòng bảng hàng đã gộp */
  tableRows: number;
}

/** Dòng bảng hàng: ≥ 3 cụm số độc lập (SL, đơn giá, thành tiền…) và ít chữ */
function isTableRow(line: string): boolean {
  const nums = line.match(/(?<![\w.])\d[\d.,]*(?![\w])/g)?.length ?? 0;
  if (nums < 3) return false;
  const words = line.replace(/[\d.,%×x*\-–/()|:]+/g, ' ').trim().split(/\s+/).filter(Boolean).length;
  return words <= nums * 3;
}

/**
 * Ẩn số tiền ([TIỀN-n]) và gộp các dòng bảng hàng liền nhau thành một dòng "[BẢNG HÀNG: n dòng, đã ẩn giá]".
 * Giữ nguyên %, số ngày, dung sai (AI cần để đánh giá điều khoản). Vừa giữ quy tắc không gửi giá nhập, vừa bớt token.
 */
export function maskContractAmounts(text: string): MaskedText {
  const amounts: Record<string, string> = {};
  let n = 0;
  let tableRows = 0;
  const out: string[] = [];
  let run = 0;
  const flush = () => {
    if (run) out.push(`[BẢNG HÀNG: ${run} dòng, đã ẩn số lượng và giá]`);
    tableRows += run;
    run = 0;
  };
  for (const line of text.split('\n')) {
    if (isTableRow(line)) {
      run++;
      continue;
    }
    flush();
    out.push(
      line.replace(MONEY, (m) => {
        const key = `[TIỀN-${++n}]`;
        amounts[key] = m;
        return key;
      }),
    );
  }
  flush();
  return { text: out.join('\n'), amounts, tableRows };
}

/** Trả số tiền thật vào câu AI viết (AI chỉ thấy [TIỀN-n]) */
export function unmaskAmounts(text: string, amounts: Record<string, string>): string {
  return text.replace(/\[TIỀN-\d+\]/g, (k) => amounts[k] ?? k);
}

/** Chữ gửi AI: "[C3] Article 3 – Payment …" từng điều, cách một dòng */
export function clausesForPrompt(clauses: readonly ContractClause[]): string {
  return clauses.map((c) => `[${c.ref}] ${c.text}`).join('\n\n');
}

// ───────────────────────── Bản sửa ─────────────────────────

/**
 * Ghép bản hợp đồng đã sửa: điều có đề nghị đã chấp nhận (hoặc chưa xem, mặc định đưa vào) thay bằng câu sửa,
 * điều thiếu thêm vào cuối thành "Điều bổ sung". Điều bị "Bỏ qua" giữ nguyên bản gốc. lang = 'vi' dùng proposalVi.
 */
export function assembleRevisedContract(clauses: readonly ContractClause[], findings: readonly ContractFinding[], useVi: boolean): string {
  const live = findings.filter((f) => f.decision !== 'rejected');
  const byRef = new Map<string, ContractFinding[]>();
  for (const f of live) if (f.ref) byRef.set(f.ref, [...(byRef.get(f.ref) ?? []), f]);
  const pick = (f: ContractFinding) => (useVi ? f.proposalVi : f.proposalPartner).trim();
  const parts = clauses.map((c) => {
    const fs = byRef.get(c.ref);
    if (!fs?.length) return c.text;
    return [c.heading, ...fs.map(pick)].join('\n');
  });
  const added = live.filter((f) => !f.ref);
  added.forEach((f, i) => parts.push(`${useVi ? 'Điều bổ sung' : 'Additional clause'} ${i + 1} – ${f.clause}\n${pick(f)}`));
  return parts.join('\n\n');
}

export interface ContractReviewSummary {
  total: number;
  high: number;
  medium: number;
  low: number;
  missing: number;
  lawyer: number;
  pending: number;
}

export function contractReviewSummary(findings: readonly ContractFinding[]): ContractReviewSummary {
  const s: ContractReviewSummary = { total: findings.length, high: 0, medium: 0, low: 0, missing: 0, lawyer: 0, pending: 0 };
  for (const f of findings) {
    s[f.risk]++;
    if (!f.ref) s.missing++;
    if (f.lawyerCheck) s.lawyer++;
    if (f.decision === 'pending') s.pending++;
  }
  return s;
}

/** Sắp điều theo mức rủi ro rồi theo thứ tự trong hợp đồng (C2 trước C10; điều thiếu xuống cuối nhóm) */
export function sortFindings<T extends Pick<ContractFinding, 'risk' | 'ref'>>(findings: readonly T[]): T[] {
  const r = (x: ClauseRisk) => CLAUSE_RISKS.indexOf(x);
  const n = (ref: string | null) => (ref ? Number(ref.slice(1)) : 1e6);
  return [...findings].sort((a, b) => r(a.risk) - r(b.risk) || n(a.ref) - n(b.ref));
}
